import React, { useMemo, useRef } from "react";
import {
  Stack,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  Box,
  useColorModeValue,
  Tooltip,
  useBreakpointValue,
  Center,
  Text,
  Image,
  useDisclosure,
  Fade,
} from "@chakra-ui/react";
import { MarkdownViewer } from "screens/markdown/MarkdownViewer";
import { useCustomScrollbar } from "hooks/useCustomScrollbar";
import { ContentPreview } from "screens/content/contentView/previewSection/ContentPreview";
import type { ContentDetails } from "types/content/ContentDetails";
import { useCollectionKey, useContents, useFilterAndGroupReports, useManualTags, useUserSubscriptionDetails } from "hooks";
import { UpgradePlanButton } from "screens/panels/settings/tabs/subscription/SubscriptionUpgrade";
import reportPlaceholder from "screens/common/static/misc/report_placeholder.png";
import { ShowMoreContainer } from "screens/common/components/ShowMoreContainer";
import { useCopyValue } from "hooks/useCopies";

interface ProjectAnalysisPopoverContentProps {
  projectId?: string;
  useReportPlaceholder?: boolean;
  companyDescription?: string;
}

export const ProjectAnalysisPopoverContent: React.FC<ProjectAnalysisPopoverContentProps> = ({
  projectId,
  useReportPlaceholder,
  companyDescription,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const { scrollbarStyle } = useCustomScrollbar(containerRef, { width: "2px", barTransparency: 0.2 });
  const { onToggle, isOpen } = useDisclosure();
  const collectionMetadataIds = useCollectionKey(projectId, "metadataIds");
  const investmentScoreAnalytical = useCollectionKey(projectId, "investmentScoreAnalytical");
  const investmentScoreSentiment = useCollectionKey(projectId, "investmentScoreSentiment");
  const subscriptionInfo = useUserSubscriptionDetails();
  const hasProfessionalFeatures = useMemo(() => subscriptionInfo?.internalPlanName !== "free_trial", [subscriptionInfo?.internalPlanName]);
  const quadrantReportMarkdown = useCopyValue("copy_markdown_deal_finder_quadrant_report_tooltip");

  const explanations = useMemo(
    () => ({
      fundamentals: investmentScoreAnalytical?.explanation,
      sentiment: investmentScoreSentiment?.explanation,
    }),
    [investmentScoreAnalytical, investmentScoreSentiment]
  );
  const tagsFilter = useManualTags();
  const contentData = useContents(collectionMetadataIds ?? [], { refreshFromNetwork: false, filterByTags: tagsFilter });
  const contentDataFiltered = useFilterAndGroupReports(contentData);
  const firstContentDetailFromFilter = useMemo(() => {
    const firstGroup = Object.values(contentDataFiltered)[0];
    return firstGroup?.[0] as ContentDetails | undefined;
  }, [contentDataFiltered]);

  const popoverBgColor = useColorModeValue("white", "gray.700");
  const tabPanelBgColor = useColorModeValue("gray.700", "gray.900");
  const footerBgColor = useColorModeValue("gray.500", "gray.900");
  const tabPanelTextFade = useColorModeValue("45,55,72", "23,25,35");
  const shareDetails = useCollectionKey(projectId, "shareDetails");
  const isSharedRead = shareDetails && shareDetails.accessMode === "read";

  const hasBothExplanations = Boolean(explanations.fundamentals?.length && explanations.sentiment?.length);

  return (
    <Stack direction={"row"} pt=".5rem" spacing="0">
      {(explanations.fundamentals?.length || explanations.sentiment?.length) && (
        <Box width={isMobile ? "100%" : "27rem"} minHeight={"22rem"}>
          <Tabs size="sm">
            <TabList color={"gray.300"} className="ch-quadrant-tab">
              {Object.keys(explanations ?? {}).map(
                (key, idx) =>
                  explanations?.[key] && (
                    <Tab _selected={{ color: "primary.default", fontWeight: "semibold", fontsize: "md" }} key={idx}>
                      {key.toUpperCase()}
                    </Tab>
                  )
              )}
            </TabList>
            <TabPanels height={"20rem"} overflow={"auto"} css={scrollbarStyle}>
              {Object.entries(explanations).map(
                ([key, explanation], idx) =>
                  explanation && (
                    <TabPanel key={idx} className="ch-quadrant-tab-panel">
                      <MarkdownViewer backgroundColor={tabPanelBgColor} content={explanation} fontSize="sm" fontColor={popoverBgColor} />
                      <Box
                        zIndex={3}
                        position="absolute"
                        bottom="0"
                        left="0"
                        right="0"
                        height="30px"
                        background={`linear-gradient(to bottom, rgba(${tabPanelTextFade},34%), rgba(${tabPanelTextFade},1))`}
                        pointerEvents="none"
                      />
                    </TabPanel>
                  )
              )}
            </TabPanels>
          </Tabs>
        </Box>
      )}
      {companyDescription && (
        <Box p="1rem" maxWidth={"20rem"}>
          <ShowMoreContainer height="16.5rem" minHeight="8rem">
            <MarkdownViewer
              blurNumbers
              backgroundColor={tabPanelBgColor}
              fontColor={popoverBgColor}
              content={companyDescription}
              fontSize="sm"
            />
          </ShowMoreContainer>
        </Box>
      )}
      {!useReportPlaceholder && firstContentDetailFromFilter && (
        <Box>
          <Tooltip
            placement="top"
            offset={[0, -100]}
            label={isSharedRead && !hasProfessionalFeatures ? "Preview First Page of Report" : "Open Report"}
            aria-label="Download Report">
            <Box width={"16rem"} pt={hasBothExplanations ? "2rem" : "1rem"} px="1rem" pb={hasBothExplanations ? "0" : "1rem"}>
              <Box position="relative">
                <ContentPreview
                  showLargeImage={isSharedRead && !hasProfessionalFeatures}
                  backgroundColor={popoverBgColor}
                  itemContent={firstContentDetailFromFilter}
                  thumbnailHeight={"300"}
                  thumbnailWidth={"230"}
                />
                {isSharedRead && !hasProfessionalFeatures && (
                  <Box
                    position="absolute"
                    borderBottomRadius={"md"}
                    left="0"
                    right="0"
                    bottom="0"
                    zIndex={2}
                    bgColor={footerBgColor}
                    onClick={(e) => e.stopPropagation()}>
                    <Center height="100%">
                      <Stack direction={"row"} spacing={2} py="2">
                        <UpgradePlanButton buttonSize="xs" style={{ width: "4rem" }} />
                        <Center>
                          <Text fontSize={"sm"} color="white" fontWeight="medium" textAlign="center">
                            to access the full report
                          </Text>
                        </Center>
                      </Stack>
                    </Center>
                  </Box>
                )}
              </Box>
            </Box>
          </Tooltip>
        </Box>
      )}
      {useReportPlaceholder && (
        <Box width={"16rem"} p="1rem" borderRadius={"md"} position="relative" overflow="hidden">
          <Fade in={!isOpen}>
            {!isOpen && (
              <Image
                src={reportPlaceholder}
                alt="Company logo"
                maxHeight={"300px"}
                objectFit="contain"
                cursor="pointer"
                onMouseEnter={() => quadrantReportMarkdown.length > 0 && onToggle()}
                _hover={quadrantReportMarkdown.length > 0 ? { opacity: 0.8, transform: "scale(1.02)" } : {}}
                transition="all 0.3s ease-in-out"
              />
            )}
          </Fade>

          <Fade in={isOpen}>
            {isOpen && (
              <Box onMouseLeave={onToggle} height="100%" position="absolute" top="1rem" left="1rem" right="1rem" bottom="1rem">
                <ShowMoreContainer height="16rem" minHeight="8rem">
                  <MarkdownViewer
                    blurNumbers
                    backgroundColor={tabPanelBgColor}
                    fontColor={popoverBgColor}
                    content={quadrantReportMarkdown}
                    fontSize="sm"
                  />
                </ShowMoreContainer>
              </Box>
            )}
          </Fade>
        </Box>
      )}
    </Stack>
  );
};
